import * as React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowLeft, CalendarDays, Clock4, Plus, Loader2, Trash2, Check, Pencil } from "lucide-react";

import {
  getCateringEvent,
  updateCateringEvent,
  createCateringEventMilestone,
  settleCateringEventMilestone,
  deleteCateringEventMilestone,
  type CateringEventStatus,
  type CateringEventOrderStatus,
  type CateringEventOrderKind,
  type CateringEventOrderLink,
  type CateringFulfillmentHealth,
  collectCateringEventPayment,
} from "@/lib/api/catering";
import { useLocaleFormatter } from "@/lib/i18n/useLocaleFormatter";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Progress } from "@/components/ui/progress";
import { useToast } from "@/components/ui/use-toast";
import { cn } from "@/lib/utils";
import { CateringEventFormDialog } from "@/features/restaurant/components/CateringEventFormDialog";

const STATUS_BADGE_CLASSES: Record<CateringEventStatus, string> = {
  draft: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:border-amber-400/40 dark:bg-amber-400/10 dark:text-amber-200",
  proposal: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:border-amber-400/40 dark:bg-amber-400/10 dark:text-amber-200",
  tentative: "border-sky-500/40 bg-sky-500/10 text-sky-700 dark:border-sky-400/40 dark:bg-sky-400/10 dark:text-sky-200",
  confirmed: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:border-emerald-400/40 dark:bg-emerald-400/10 dark:text-emerald-200",
  in_fulfillment:
    "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:border-emerald-400/40 dark:bg-emerald-400/10 dark:text-emerald-200",
  completed: "border-emerald-600/50 bg-emerald-600/15 text-emerald-800 dark:border-emerald-400/50 dark:bg-emerald-500/10 dark:text-emerald-200",
  cancelled: "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:border-rose-400/40 dark:bg-rose-400/10 dark:text-rose-200",
};

const StatusBadge: React.FC<{ status: CateringEventStatus; label: string }> = ({ status, label }) => (
  <Badge variant="outline" className={cn("border text-xs font-medium", STATUS_BADGE_CLASSES[status])}>
    {label}
  </Badge>
);

const FULFILLMENT_HEALTH_LABELS: Record<CateringFulfillmentHealth, string> = {
  onTrack: "On track",
  attentionNeeded: "Attention needed",
  blocked: "Action required",
};

const FULFILLMENT_HEALTH_CLASSES: Record<CateringFulfillmentHealth, string> = {
  onTrack: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:border-emerald-400/40 dark:bg-emerald-400/10 dark:text-emerald-100",
  attentionNeeded:
    "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:border-amber-400/40 dark:bg-amber-400/10 dark:text-amber-100",
  blocked:
    "border-rose-500/40 bg-rose-500/10 text-rose-700 dark:border-rose-400/40 dark:bg-rose-400/10 dark:text-rose-100",
};

const ORDER_STATUS_LABELS: Record<CateringEventOrderStatus, string> = {
  pending: "Pending",
  scheduled: "Scheduled",
  in_progress: "In progress",
  fulfilled: "Fulfilled",
  cancelled: "Cancelled",
  refunded: "Refunded",
};

const ORDER_KIND_OPTIONS: CateringEventOrderKind[] = ["deposit", "balance", "add_on", "external", "internal"];

const AddPaymentFormSchema = z.object({
  kind: z.enum(["deposit", "balance", "add_on", "external", "internal"] as [CateringEventOrderKind, ...CateringEventOrderKind[]]),
  amount: z.string().trim().min(1),
  dueDate: z.string().trim().optional(),
  notes: z.string().trim().max(400).optional(),
});

type AddPaymentFormValues = z.infer<typeof AddPaymentFormSchema>;

const toEpochFromDateInput = (value: string | undefined | null): number | null => {
  if (!value) {
    return null;
  }
  const parsed = Date.parse(`${value}T00:00:00Z`);
  return Number.isNaN(parsed) ? null : parsed;
};

const parseAmountToMinorUnits = (value: string): number => {
  const normalized = value.replace(/[^0-9.-]/g, "");
  const parsed = Number.parseFloat(normalized);
  if (Number.isNaN(parsed) || parsed <= 0) {
    throw new Error("invalid_amount");
  }
  return Math.round(parsed * 100);
};

const AddPaymentDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  defaultCurrency: string;
  onSuccess: () => void;
}> = ({ open, onOpenChange, eventId, defaultCurrency, onSuccess }) => {
  const { t } = useTranslation(["events"]);
  const { toast } = useToast();
  const form = useForm<AddPaymentFormValues>({
    resolver: zodResolver(AddPaymentFormSchema),
    defaultValues: {
      kind: "deposit",
    },
  });

  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({ eventId, payload }: { eventId: string; payload: Parameters<typeof createCateringEventMilestone>[1] }) =>
      createCateringEventMilestone(eventId, payload),
    onSuccess: () => {
      toast({
        title: t("events:payments.addSuccess", { defaultValue: "Payment added" }),
      });
      form.reset({ kind: "deposit" });
      onOpenChange(false);
      onSuccess();
      void queryClient.invalidateQueries({ queryKey: ["catering", "events"] });
    },
    onError: (error: unknown) => {
      toast({
        title: t("events:payments.addError", { defaultValue: "Unable to add payment" }),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    },
  });

  const onSubmit = (values: AddPaymentFormValues) => {
    let amountCents: number;
    try {
      amountCents = parseAmountToMinorUnits(values.amount);
    } catch {
      form.setError("amount", { message: t("events:payments.validation.amount", { defaultValue: "Amount must be greater than zero" }) });
      return;
    }

    const dueAt = toEpochFromDateInput(values.dueDate);

    mutation.mutate({
      eventId,
      payload: {
        kind: values.kind,
        amountCents,
        currency: defaultCurrency,
        dueAt: dueAt ?? undefined,
        notes: values.notes?.trim() || undefined,
      },
    });
  };

  const isSubmitting = mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t("events:payments.addTitle", { defaultValue: "Add payment milestone" })}</DialogTitle>
          <DialogDescription>
            {t("events:payments.addSubtitle", { defaultValue: "Capture deposits, balances, or one-off adjustments." })}
          </DialogDescription>
        </DialogHeader>
        <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid gap-2">
            <Label>{t("events:payments.fields.kind", { defaultValue: "Payment kind" })}</Label>
            <Select
              disabled={isSubmitting}
              defaultValue={form.getValues("kind")}
              onValueChange={(value) => form.setValue("kind", value as CateringEventOrderKind)}
            >
              <SelectTrigger>
                <SelectValue placeholder={t("events:payments.fields.kindPlaceholder", { defaultValue: "Select kind" })} />
              </SelectTrigger>
              <SelectContent>
                {ORDER_KIND_OPTIONS.map((option) => (
                  <SelectItem key={option} value={option}>
                    {t(`events:payments.kind.${option}`, { defaultValue: option })}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="payment-amount">{t("events:payments.fields.amount", { defaultValue: "Amount" })}</Label>
            <Input id="payment-amount" type="number" min={0} step="0.01" disabled={isSubmitting} {...form.register("amount")} />
            {form.formState.errors.amount ? (
              <p className="text-xs text-rose-500">{form.formState.errors.amount.message}</p>
            ) : null}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="payment-due">{t("events:payments.fields.dueDate", { defaultValue: "Due date" })}</Label>
            <Input id="payment-due" type="date" disabled={isSubmitting} {...form.register("dueDate")} />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="payment-notes">{t("events:payments.fields.notes", { defaultValue: "Notes" })}</Label>
            <Textarea id="payment-notes" rows={3} disabled={isSubmitting} {...form.register("notes")} />
          </div>
          <DialogFooter className="mt-2 flex flex-col gap-2 sm:flex-row sm:justify-end">
            <Button type="button" variant="outline" disabled={isSubmitting} onClick={() => onOpenChange(false)}>
              {t("events:payments.actions.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? t("events:payments.actions.saving", { defaultValue: "Saving..." }) : t("events:payments.actions.submit", { defaultValue: "Add payment" })}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const CollectPaymentFormSchema = z
  .object({
    paymentMethodId: z.string().trim().optional(),
    paymentIntentId: z.string().trim().optional(),
    captureMethod: z.enum(["automatic", "manual"]).optional(),
    customerEmail: z.string().trim().email().optional(),
  })
  .refine((value) => Boolean(value.paymentMethodId || value.paymentIntentId), {
    message: "payment_reference_required",
    path: ["paymentMethodId"],
  });

type CollectPaymentFormValues = z.infer<typeof CollectPaymentFormSchema>;

const CollectPaymentDialog: React.FC<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  eventId: string;
  order: CateringEventOrderLink | null;
  defaultEmail?: string | null;
  onSuccess: () => void;
}> = ({ open, onOpenChange, eventId, order, defaultEmail, onSuccess }) => {
  const { t } = useTranslation(["events"]);
  const { toast } = useToast();
  const form = useForm<CollectPaymentFormValues>({
    resolver: zodResolver(CollectPaymentFormSchema),
    defaultValues: {
      paymentIntentId: order?.paymentIntentId ?? undefined,
      customerEmail: defaultEmail ?? undefined,
      captureMethod: "automatic",
    },
  });

  React.useEffect(() => {
    if (order) {
      form.reset({
        paymentMethodId: undefined,
        paymentIntentId: order.paymentIntentId ?? undefined,
        customerEmail: defaultEmail ?? undefined,
        captureMethod: "automatic",
      });
    }
  }, [order, defaultEmail, form]);

  const mutation = useMutation({
    mutationFn: (values: CollectPaymentFormValues) => {
      if (!order) {
        throw new Error("missing_order");
      }
      return collectCateringEventPayment(eventId, order.id, values);
    },
    onSuccess: (data) => {
      toast({
        title: t("events:payments.collectSuccess", { defaultValue: "Payment synced" }),
        description: data.payment.requiresAction
          ? t("events:payments.collectRequireAction", { defaultValue: "Stripe requires customer action to complete." })
          : undefined,
      });
      form.reset();
      onSuccess();
      onOpenChange(false);
    },
    onError: (error: unknown) => {
      toast({
        title: t("events:payments.collectError", { defaultValue: "Unable to sync payment" }),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    },
  });

  if (!order) {
    return null;
  }

  const onSubmit = (values: CollectPaymentFormValues) => {
    mutation.mutate(values);
  };

  const isSubmitting = mutation.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>{t("events:payments.collectTitle", { defaultValue: "Collect payment" })}</DialogTitle>
          <DialogDescription>
            {t("events:payments.collectSubtitle", {
              defaultValue: "Charge a saved card, reuse an intent, or sync a manual Stripe payment.",
            })}
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-md border border-dashed border-neutral-200 bg-neutral-50/70 p-3 text-xs text-neutral-600 dark:border-neutral-800 dark:bg-neutral-900/40 dark:text-neutral-400">
          <div className="flex flex-col gap-1">
            <span className="font-semibold text-neutral-700 dark:text-neutral-200">
              {t("events:payments.currentProcessorStatus", { defaultValue: "Current status" })}
            </span>
            <span>
              {order.paymentStatus
                ? t("events:payments.processorStatus", {
                    defaultValue: "Stripe status: {{status}}",
                    status: order.paymentStatus,
                  })
                : t("events:payments.processorStatusUnknown", { defaultValue: "No Stripe status recorded." })}
            </span>
            {order.paymentIntentId ? (
              <span className="break-all text-xs text-neutral-500 dark:text-neutral-400">
                {t("events:payments.intentLabel", { defaultValue: "Existing intent" })}: {order.paymentIntentId}
              </span>
            ) : null}
          </div>
        </div>
        <form className="flex flex-col gap-4" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="grid gap-2">
            <Label htmlFor="payment-method-id">{t("events:payments.fields.paymentMethod", { defaultValue: "Payment method ID" })}</Label>
            <Input
              id="payment-method-id"
              placeholder="pm_..."
              disabled={isSubmitting}
              {...form.register("paymentMethodId")}
            />
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {t("events:payments.collectHelperPaymentMethod", {
                defaultValue: "Use a stored Stripe payment method to immediately charge the card.",
              })}
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="payment-intent-id">{t("events:payments.fields.paymentIntent", { defaultValue: "Payment intent ID" })}</Label>
            <Input
              id="payment-intent-id"
              placeholder="pi_..."
              disabled={isSubmitting}
              {...form.register("paymentIntentId")}
            />
            <p className="text-xs text-neutral-500 dark:text-neutral-400">
              {t("events:payments.collectHelperIntent", {
                defaultValue: "Attach an existing intent when checkout happened outside Braise.",
              })}
            </p>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="payment-capture">{t("events:payments.fields.captureMethod", { defaultValue: "Capture" })}</Label>
            <Select
              disabled={isSubmitting}
              defaultValue={form.getValues("captureMethod") ?? "automatic"}
              onValueChange={(value) => form.setValue("captureMethod", value as "automatic" | "manual")}
            >
              <SelectTrigger id="payment-capture">
                <SelectValue placeholder={t("events:payments.collectSelectCapture", { defaultValue: "Select capture method" })} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="automatic">{t("events:payments.collectCaptureAutomatic", { defaultValue: "Automatic" })}</SelectItem>
                <SelectItem value="manual">{t("events:payments.collectCaptureManual", { defaultValue: "Manual" })}</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="grid gap-2">
            <Label htmlFor="payment-email">{t("events:payments.fields.customerEmail", { defaultValue: "Customer email" })}</Label>
            <Input
              id="payment-email"
              type="email"
              placeholder="guest@example.com"
              disabled={isSubmitting}
              {...form.register("customerEmail")}
            />
          </div>
          {form.formState.errors.paymentMethodId?.message === "payment_reference_required" ? (
            <p className="text-sm text-destructive">
              {t("events:payments.collectValidation.reference", { defaultValue: "Provide a payment method or intent." })}
            </p>
          ) : null}
          <DialogFooter className="flex gap-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={isSubmitting}>
              {t("events:payments.actions.cancel", { defaultValue: "Cancel" })}
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("events:payments.collecting", { defaultValue: "Syncing..." })}
                </>
              ) : (
                t("events:payments.collectSubmit", { defaultValue: "Sync payment" })
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const eventDetailQueryKey = (eventId: string | undefined) => ["catering", "event", eventId] as const;

const EventDetailPageV3: React.FC = () => {
  const { t } = useTranslation(["events", "common"]);
  const { formatDate, formatCurrency, formatNumber } = useLocaleFormatter();
  const { eventId } = useParams<{ eventId: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isAddPaymentOpen, setIsAddPaymentOpen] = React.useState(false);
  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [collectPaymentTarget, setCollectPaymentTarget] = React.useState<CateringEventOrderLink | null>(null);

  const detailQuery = useQuery({
    queryKey: eventDetailQueryKey(eventId),
    enabled: Boolean(eventId),
    queryFn: () => getCateringEvent(eventId ?? ""),
  });

  const event = detailQuery.data?.event;

  const statusMutation = useMutation({
    mutationFn: (status: CateringEventStatus) => updateCateringEvent(eventId ?? "", { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: eventDetailQueryKey(eventId) });
      void queryClient.invalidateQueries({ queryKey: ["catering", "events"] });
      toast({ title: t("events:toasts.statusUpdated", { defaultValue: "Status updated" }) });
    },
    onError: (error: unknown) => {
      toast({
        title: t("events:toasts.statusError", { defaultValue: "Unable to update status" }),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    },
  });

  const settleMutation = useMutation({
    mutationFn: (linkId: string) => settleCateringEventMilestone(eventId ?? "", linkId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: eventDetailQueryKey(eventId) });
      void queryClient.invalidateQueries({ queryKey: ["catering", "events"] });
      toast({ title: t("events:payments.settleSuccess", { defaultValue: "Payment marked as fulfilled" }) });
    },
    onError: (error: unknown) => {
      toast({
        title: t("events:payments.settleError", { defaultValue: "Unable to mark payment" }),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (linkId: string) => deleteCateringEventMilestone(eventId ?? "", linkId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: eventDetailQueryKey(eventId) });
      void queryClient.invalidateQueries({ queryKey: ["catering", "events"] });
      toast({ title: t("events:payments.deleteSuccess", { defaultValue: "Payment removed" }) });
    },
    onError: (error: unknown) => {
      toast({
        title: t("events:payments.deleteError", { defaultValue: "Unable to remove payment" }),
        description: error instanceof Error ? error.message : undefined,
        variant: "destructive",
      });
    },
  });

  const isLoading = detailQuery.isLoading;
  const isError = detailQuery.isError;

  const handleStatusChange = (status: CateringEventStatus) => {
    statusMutation.mutate(status);
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-4 px-4 py-6 md:px-6 lg:px-8">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-40 w-full" />
        <Skeleton className="h-40 w-full" />
      </div>
    );
  }

  if (isError || !event) {
    return (
      <div className="px-4 py-6 md:px-6 lg:px-8">
        <Alert variant="destructive">
          <AlertDescription>
            {t("events:messages.detailError", { defaultValue: "We couldn’t load this event. Try again." })}
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  const fulfillment = event.fulfillment;
  const completionPercent = fulfillment.totalMilestones > 0 ? Math.round((fulfillment.completed / fulfillment.totalMilestones) * 100) : 0;
  const fulfillmentHealthLabel = t(`events:detail.fulfillment.health.${fulfillment.health}`, {
    defaultValue: FULFILLMENT_HEALTH_LABELS[fulfillment.health],
  });
  const nextDueLabel = fulfillment.nextDueAt
    ? formatDate(fulfillment.nextDueAt)
    : t("events:detail.fulfillment.noDue", { defaultValue: "None scheduled" });
  const fulfillmentMetrics = [
    {
      key: "open",
      label: t("events:detail.fulfillment.metrics.open", { defaultValue: "Open" }),
      value: formatNumber(fulfillment.open),
    },
    {
      key: "dueSoon",
      label: t("events:detail.fulfillment.metrics.dueSoon", { defaultValue: "Due soon" }),
      value: formatNumber(fulfillment.dueSoon),
    },
    {
      key: "overdue",
      label: t("events:detail.fulfillment.metrics.overdue", { defaultValue: "Overdue" }),
      value: formatNumber(fulfillment.overdue),
    },
    {
      key: "nextDue",
      label: t("events:detail.fulfillment.metrics.nextDue", { defaultValue: "Next due" }),
      value: nextDueLabel,
    },
  ] as const;

  const statusLabel = t(`events:status.${event.status}`, { defaultValue: event.status });
  const serviceStyleLabel = t(`events:serviceStyle.${event.serviceStyle}`, {
    defaultValue: event.serviceStyle,
  });
  const currency = event.currency ?? "USD";

  return (
    <div className="flex flex-col gap-6 px-4 py-6 md:px-6 lg:px-8">
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("events:detail.actions.back", { defaultValue: "Back to events" })}
          </Button>
          <StatusBadge status={event.status as CateringEventStatus} label={statusLabel} />
          <Select
            defaultValue={event.status}
            onValueChange={(value) => handleStatusChange(value as CateringEventStatus)}
            disabled={statusMutation.isPending}
          >
            <SelectTrigger className="w-48 border-neutral-200 dark:border-neutral-700">
              <SelectValue placeholder={statusLabel} />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(STATUS_BADGE_CLASSES) as CateringEventStatus[]).map((status) => (
                <SelectItem key={status} value={status}>
                  {t(`events:status.${status}`, { defaultValue: status })}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {statusMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin text-neutral-400" /> : null}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setIsEditOpen(true)}
            aria-label={t("events:detail.actions.edit", { defaultValue: "Edit event" })}
          >
            <Pencil className="mr-2 h-4 w-4" />
            {t("events:detail.actions.edit", { defaultValue: "Edit event" })}
          </Button>
        </div>
        <div className="flex flex-col gap-1">
          <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">{event.name}</h1>
          <p className="text-sm text-neutral-600 dark:text-neutral-400">{serviceStyleLabel}</p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300">
            <CalendarDays className="h-4 w-4" />
            <span>{formatDate(event.eventDate)}</span>
          </div>
          {event.serviceWindowStart ? (
            <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-neutral-300">
              <Clock4 className="h-4 w-4" />
              <span>
                {formatDate(event.serviceWindowStart)}
                {event.serviceWindowEnd ? ` → ${formatDate(event.serviceWindowEnd)}` : ""}
              </span>
            </div>
          ) : null}
        </div>
      </div>

      <div className="grid gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader>
            <CardTitle>{t("events:detail.contact.title", { defaultValue: "Point of contact" })}</CardTitle>
            <CardDescription>
              {t("events:detail.contact.subtitle", { defaultValue: "Keep client information up to date." })}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-2 text-sm">
            <div>
              <p className="font-medium text-neutral-700 dark:text-neutral-200">
                {event.contactName ?? t("events:detail.contact.empty", { defaultValue: "No contact name" })}
              </p>
              <p className="text-neutral-500 dark:text-neutral-400">{event.contactEmail ?? "--"}</p>
              <p className="text-neutral-500 dark:text-neutral-400">{event.contactPhone ?? "--"}</p>
            </div>
            <Separator className="my-2" />
            <div className="grid grid-cols-2 gap-3 text-sm text-neutral-600 dark:text-neutral-300">
              <div className="flex flex-col gap-1">
                <span className="font-medium text-neutral-900 dark:text-neutral-100">
                  {t("events:list.budget", { defaultValue: "Budget" })}
                </span>
                <span>{formatCurrency(event.costEstimate.revenueCents, currency, { isMinorUnit: true })}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-medium text-neutral-900 dark:text-neutral-100">
                  {t("events:list.depositDue", { defaultValue: "Deposit due" })}
                </span>
                <span>{event.depositDueAt ? formatDate(event.depositDueAt) : "--"}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-medium text-neutral-900 dark:text-neutral-100">
                  {t("events:list.finalDue", { defaultValue: "Final payment" })}
                </span>
                <span>{event.finalPaymentDueAt ? formatDate(event.finalPaymentDueAt) : "--"}</span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="font-medium text-neutral-900 dark:text-neutral-100">
                  {t("events:detail.contact.leadSource", { defaultValue: "Lead source" })}
                </span>
                <span>{event.leadSource ?? "--"}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("events:detail.schedule.title", { defaultValue: "Schedule" })}</CardTitle>
            <CardDescription>
              {t("events:detail.schedule.subtitle", { defaultValue: "Prep and service windows at a glance." })}
            </CardDescription>
          </CardHeader>
          <CardContent className="grid gap-3 text-sm text-neutral-600 dark:text-neutral-300">
            <div className="flex items-center justify-between">
              <span className="font-medium text-neutral-900 dark:text-neutral-100">{t("events:detail.schedule.duration", { defaultValue: "Duration" })}</span>
              <span>{formatNumber(event.scheduleSummary.durationMinutes)} {t("events:list.minutes", { defaultValue: "min" })}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-medium text-neutral-900 dark:text-neutral-100">{t("events:detail.schedule.prep", { defaultValue: "Prep window" })}</span>
              <span>{formatNumber(event.scheduleSummary.prepWindowMinutes)} {t("events:list.minutes", { defaultValue: "min" })}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-medium text-neutral-900 dark:text-neutral-100">{t("events:detail.schedule.service", { defaultValue: "Service window" })}</span>
              <span>{formatNumber(event.scheduleSummary.serviceWindowMinutes)} {t("events:list.minutes", { defaultValue: "min" })}</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t("events:detail.staffing.title", { defaultValue: "Staffing & Cost" })}</CardTitle>
            <CardDescription>{t("events:detail.staffing.subtitle", { defaultValue: "Recommended headcount and labor snapshot." })}</CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3 text-sm text-neutral-600 dark:text-neutral-300">
            <div className="flex items-center justify-between">
              <span className="font-medium text-neutral-900 dark:text-neutral-100">{t("events:detail.staffing.total", { defaultValue: "Total staff" })}</span>
              <span>{formatNumber(event.staffing.total)}</span>
            </div>
            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="flex flex-col">
                <span className="text-neutral-500 dark:text-neutral-400">{t("events:detail.staffing.bartenders", { defaultValue: "Bartenders" })}</span>
                <span className="font-medium text-neutral-800 dark:text-neutral-200">{formatNumber(event.staffing.bartenders)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-neutral-500 dark:text-neutral-400">{t("events:detail.staffing.servers", { defaultValue: "Servers" })}</span>
                <span className="font-medium text-neutral-800 dark:text-neutral-200">{formatNumber(event.staffing.servers)}</span>
              </div>
              <div className="flex flex-col">
                <span className="text-neutral-500 dark:text-neutral-400">{t("events:detail.staffing.support", { defaultValue: "Support" })}</span>
                <span className="font-medium text-neutral-800 dark:text-neutral-200">{formatNumber(event.staffing.support)}</span>
              </div>
            </div>
            <Separator className="my-1" />
            <div className="flex items-center justify-between">
              <span className="font-medium text-neutral-900 dark:text-neutral-100">{t("events:detail.cost.revenue", { defaultValue: "Projected revenue" })}</span>
              <span>{formatCurrency(event.costEstimate.revenueCents, currency, { isMinorUnit: true })}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-medium text-neutral-900 dark:text-neutral-100">{t("events:detail.cost.labor", { defaultValue: "Labor estimate" })}</span>
              <span>{formatCurrency(event.costEstimate.laborCents, currency, { isMinorUnit: true })}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="font-medium text-neutral-900 dark:text-neutral-100">{t("events:detail.cost.margin", { defaultValue: "Projected margin" })}</span>
              <span className={cn(event.costEstimate.marginCents < 0 ? "text-rose-600 dark:text-rose-300" : "text-emerald-600 dark:text-emerald-300")}>
                {formatCurrency(event.costEstimate.marginCents, currency, { isMinorUnit: true })}
              </span>
            </div>
          </CardContent>
        </Card>

        <Card className="lg:col-span-3">
          <CardHeader>
            <CardTitle>{t("events:detail.fulfillment.title", { defaultValue: "Fulfillment" })}</CardTitle>
            <CardDescription>
              {t("events:detail.fulfillment.subtitle", { defaultValue: "Milestone completion and payment health." })}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-4 text-sm text-neutral-600 dark:text-neutral-300">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Badge
                  variant="outline"
                  className={cn("border text-xs font-medium", FULFILLMENT_HEALTH_CLASSES[fulfillment.health])}
                >
                  {fulfillmentHealthLabel}
                </Badge>
                <span className="text-neutral-600 dark:text-neutral-300">
                  {t("events:detail.fulfillment.description", {
                    defaultValue: "Track milestone health and upcoming deadlines.",
                  })}
                </span>
              </div>
              <div className="text-right">
                <span className="text-sm font-medium text-neutral-900 dark:text-neutral-100">
                  {t("events:detail.fulfillment.total", {
                    defaultValue: "{{count}} milestones",
                    count: formatNumber(fulfillment.totalMilestones),
                  })}
                </span>
              </div>
            </div>
            {fulfillment.totalMilestones === 0 ? (
              <div className="flex items-center justify-between rounded-md border border-dashed border-neutral-200 bg-neutral-50/60 p-4 text-sm text-neutral-500 dark:border-neutral-800 dark:bg-neutral-900/40 dark:text-neutral-400">
                {t("events:detail.fulfillment.empty", { defaultValue: "No payment milestones yet." })}
              </div>
            ) : (
              <>
                <div>
                  <div className="mb-2 flex items-center justify-between text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                    <span>{t("events:detail.fulfillment.progress", { defaultValue: "Completed" })}</span>
                    <span>{completionPercent}%</span>
                  </div>
                  <Progress value={completionPercent} className="h-2" />
                  <p className="mt-2 text-xs text-neutral-500 dark:text-neutral-400">
                    {t("events:detail.fulfillment.completedOutOf", {
                      defaultValue: "{{completed}} of {{total}} milestones",
                      completed: formatNumber(fulfillment.completed),
                      total: formatNumber(fulfillment.totalMilestones),
                    })}
                  </p>
                </div>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                  {fulfillmentMetrics.map((metric) => (
                    <div
                      key={metric.key}
                      className="flex flex-col rounded-lg border border-neutral-200/70 bg-white/70 p-3 dark:border-neutral-800/70 dark:bg-neutral-900/40"
                    >
                      <span className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                        {metric.label}
                      </span>
                      <span className="text-base font-semibold text-neutral-900 dark:text-neutral-100">{metric.value}</span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>{t("events:payments.title", { defaultValue: "Payment schedule" })}</CardTitle>
            <CardDescription>
              {t("events:payments.subtitle", { defaultValue: "Track deposits and balances before the event." })}
            </CardDescription>
          </div>
          <Button type="button" size="sm" onClick={() => setIsAddPaymentOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            {t("events:payments.addAction", { defaultValue: "Add payment" })}
          </Button>
        </CardHeader>
        <CardContent className="flex flex-col gap-3">
          {event.orders.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-neutral-200/80 px-4 py-6 text-center text-sm text-neutral-500 dark:border-neutral-800/70 dark:text-neutral-400">
              {t("events:payments.empty", { defaultValue: "No payments scheduled yet." })}
            </div>
          ) : (
            event.orders.map((order) => {
              const statusLabel = t(`events:payments.status.${order.status}`, {
                defaultValue: ORDER_STATUS_LABELS[order.status],
              });
              const dueStatusLabel = order.dueStatus
                ? t(`events:payments.dueState.${order.dueStatus}`, { defaultValue: order.dueStatus })
                : null;
              const settledLabel = order.settledAt ? formatDate(order.settledAt) : null;
              const isFulfilled = order.status === "fulfilled";

              return (
                <div
                  key={order.id}
                  className="flex flex-col gap-3 rounded-lg border border-neutral-200/80 bg-white/60 p-4 dark:border-neutral-800/70 dark:bg-neutral-900/40"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="border border-neutral-200 text-xs font-medium dark:border-neutral-700">
                      {t(`events:payments.kind.${order.kind}`, { defaultValue: order.kind })}
                    </Badge>
                    <Badge variant="outline" className="border border-neutral-200 text-xs font-medium dark:border-neutral-700">
                      {statusLabel}
                    </Badge>
                    {dueStatusLabel ? (
                      <Badge
                        variant="secondary"
                        className={cn(
                          "border text-xs font-medium",
                          order.dueStatus === "dueSoon"
                            ? "border-amber-500/40 bg-amber-500/15 text-amber-700 dark:border-amber-400/40 dark:bg-amber-400/15 dark:text-amber-100"
                            : "border-rose-500/40 bg-rose-500/15 text-rose-700 dark:border-rose-400/40 dark:bg-rose-400/15 dark:text-rose-100",
                        )}
                      >
                        {dueStatusLabel}
                      </Badge>
                    ) : null}
                    {settledLabel ? (
                      <Badge variant="outline" className="border border-emerald-500/40 bg-emerald-500/10 text-xs font-medium text-emerald-700 dark:border-emerald-400/40 dark:bg-emerald-400/10 dark:text-emerald-100">
                        {t("events:payments.settledOn", { defaultValue: "Settled {{date}}", date: settledLabel })}
                      </Badge>
                    ) : null}
                  </div>
                  <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex flex-col gap-1">
                      <span className="text-base font-semibold text-neutral-900 dark:text-neutral-50">
                        {formatCurrency(order.amountCents, order.currency ?? currency, { isMinorUnit: true })}
                      </span>
                      <span className="text-sm text-neutral-600 dark:text-neutral-400">
                        {order.dueAt ? formatDate(order.dueAt) : t("events:payments.noDue", { defaultValue: "No due date" })}
                      </span>
                      {order.notes ? (
                        <span className="text-sm text-neutral-600 dark:text-neutral-400">{order.notes}</span>
                      ) : null}
                      {order.paymentStatus ? (
                        <span className="text-xs uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                          {t("events:payments.processorStatus", {
                            defaultValue: "Stripe status: {{status}}",
                            status: order.paymentStatus,
                          })}
                        </span>
                      ) : null}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={isFulfilled}
                        onClick={() => setCollectPaymentTarget(order)}
                      >
                        {t("events:payments.collect", { defaultValue: "Collect payment" })}
                      </Button>
                      <Button
                        type="button"
                        variant="secondary"
                        size="sm"
                        disabled={isFulfilled || settleMutation.isPending}
                        onClick={() => settleMutation.mutate(order.id)}
                      >
                        {settleMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                        <span>{t("events:payments.markPaid", { defaultValue: "Mark paid" })}</span>
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={deleteMutation.isPending}
                        onClick={() => deleteMutation.mutate(order.id)}
                      >
                        <Trash2 className="h-4 w-4 text-neutral-500 hover:text-rose-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      <AddPaymentDialog
        open={isAddPaymentOpen}
        onOpenChange={setIsAddPaymentOpen}
        eventId={event.id}
        defaultCurrency={currency}
        onSuccess={() => {
          void queryClient.invalidateQueries({ queryKey: eventDetailQueryKey(eventId) });
        }}
      />
      <CollectPaymentDialog
        open={Boolean(collectPaymentTarget)}
        onOpenChange={(open) => {
          if (!open) {
            setCollectPaymentTarget(null);
          }
        }}
        eventId={event.id}
        order={collectPaymentTarget}
        defaultEmail={event.contactEmail ?? undefined}
        onSuccess={() => {
          void queryClient.invalidateQueries({ queryKey: eventDetailQueryKey(eventId) });
          void queryClient.invalidateQueries({ queryKey: ["catering", "events"] });
        }}
      />
      <CateringEventFormDialog
        mode="edit"
        open={isEditOpen}
        onOpenChange={setIsEditOpen}
        eventId={event.id}
        event={event}
        onSuccess={() => {
          void queryClient.invalidateQueries({ queryKey: eventDetailQueryKey(eventId) });
          void queryClient.invalidateQueries({ queryKey: ["catering", "events"] });
        }}
      />
    </div>
  );
};

export default EventDetailPageV3;
